// src/services/api.js
import axios from 'axios';

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCTION SETUP:
// In your server .env file set ONE of these:
//   REACT_APP_API_URL=https://freshhillsmilk.in/api     ← recommended
// Then run: npm run build
// ─────────────────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// BASE_ORIGIN
// Strips /api from API_URL to get the server root origin.
//   REACT_APP_API_URL=https://freshhillsmilk.in/api
//   → BASE_ORIGIN = https://freshhillsmilk.in
//
//   REACT_APP_API_URL=http://localhost:5000/api  (local dev)
//   → BASE_ORIGIN = http://localhost:5000
// ─────────────────────────────────────────────────────────────────────────────
const BASE_ORIGIN = API_URL
  .replace(/\/api\/?$/, '')
  .replace(/\/$/, '');

// ─────────────────────────────────────────────────────────────────────────────
// resolveImageUrl
// Rebuilds any image URL to use the correct production host.
// Handles: full URLs (http/https), /api/uploads/..., /uploads/..., bare filenames
// ─────────────────────────────────────────────────────────────────────────────
export const resolveImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string' || !imageUrl.trim()) return null;
  const url = imageUrl.trim();

  // Full URL — always strip host and rebuild with BASE_ORIGIN
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const parsed = new URL(url);
      return `${BASE_ORIGIN}${parsed.pathname}`;
    } catch {
      return url;
    }
  }

  // /api/uploads/... → strip /api prefix
  if (url.startsWith('/api/uploads/')) {
    return `${BASE_ORIGIN}${url.replace('/api', '')}`;
  }

  // /uploads/...
  if (url.startsWith('/uploads/')) {
    return `${BASE_ORIGIN}${url}`;
  }

  // uploads/... (no leading slash)
  if (url.startsWith('uploads/')) {
    return `${BASE_ORIGIN}/${url}`;
  }

  // Bare filename: abc.png → assume products folder
  if (!url.includes('/')) {
    return `${BASE_ORIGIN}/uploads/products/${url}`;
  }

  return `${BASE_ORIGIN}/${url.replace(/^\//, '')}`;
};

export const resolveUploadUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      const parsed = new URL(path);
      return `${BASE_ORIGIN}${parsed.pathname}`;
    } catch { return path; }
  }
  return `${BASE_ORIGIN}/${path.replace(/^\//, '')}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCTS
// ─────────────────────────────────────────────────────────────────────────────
export const getProducts   = ()           => api.get('/products');
export const getProduct    = (id)         => api.get(`/products/${id}`);
export const createProduct = (fd)         => api.post('/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateProduct = (id, fd)     => api.put(`/products/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteProduct = (id)         => api.delete(`/products/${id}`);

// ─────────────────────────────────────────────────────────────────────────────
// FRIDGES
// ─────────────────────────────────────────────────────────────────────────────
export const getFridges         = ()        => api.get('/fridges');
export const getFridgeById      = (id)      => api.get(`/fridges/${id}`);
export const createFridge       = (d)       => api.post('/fridges', d);
export const updateFridge       = (id, d)   => api.put(`/fridges/${id}`, d);
export const deleteFridge       = (id)      => api.delete(`/fridges/${id}`);
export const assignProduct      = (d)       => api.post('/fridges/assign-product', d);
export const getFridgeProducts  = (id)      => api.get(`/fridges/${id}/products`);
export const getLowStockFridges = ()        => api.get('/fridges/low-stock');

// ─────────────────────────────────────────────────────────────────────────────
// APARTMENTS
// ─────────────────────────────────────────────────────────────────────────────
export const getApartments    = ()        => api.get('/apartments');
export const getApartmentById = (id)      => api.get(`/apartments/${id}`);
export const createApartment  = (d)       => api.post('/apartments', d);
export const updateApartment  = (id, d)   => api.put(`/apartments/${id}`, d);
export const deleteApartment  = (id)      => api.delete(`/apartments/${id}`);

// ─────────────────────────────────────────────────────────────────────────────
// QR / CUSTOMER FACING
// ─────────────────────────────────────────────────────────────────────────────
export const scanFridge     = (fridgeId) => api.get(`/fridge/scan/${fridgeId}`);
export const fridgeCheckout = (d)        => api.post('/fridge/checkout', d);

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMERS
// ─────────────────────────────────────────────────────────────────────────────
export const registerCustomer   = (d)       => api.post('/customer/register', d);
export const getCustomer        = (id)      => api.get(`/customer/${id}`);
export const getCustomerByEmail = (email)   => api.get(`/customer/email/${email}`);
export const updateCustomer     = (id, d)   => api.put(`/customer/${id}`, d);
export const getCustomerOrders  = (id)      => api.get(`/customer/${id}/orders`);
export const customerCheckout   = (d)       => api.post('/customer/checkout', d);

// ─────────────────────────────────────────────────────────────────────────────
// ORDERS / SALES
// ─────────────────────────────────────────────────────────────────────────────
export const getOrders     = ()      => api.get('/orders');
export const getDailySales = (date)  => api.get('/sales/daily', { params: { date } });

// ─────────────────────────────────────────────────────────────────────────────
// STOCK MONITORING
// ─────────────────────────────────────────────────────────────────────────────
export const getStock        = (status)  => api.get('/stock', { params: { status } });
export const getStockSummary = ()        => api.get('/stock/summary');
export const restockItem     = (id, qty) => api.post(`/stock/${id}/restock`, { qty });

// Stock Manager (AssignProducts → Stock Manager tab)
export const getFridgesStock   = ()                  => api.get('/stock/fridges');
export const setStockQty       = (stockId, qty)      => api.put(`/stock/${stockId}/set`, { quantity: qty });
export const bulkRestockFridge = (fridgeId, items)   => api.post(`/stock/fridges/${fridgeId}/bulk-restock`, { items });

// ─────────────────────────────────────────────────────────────────────────────
// REFILL SCHEDULES
// ─────────────────────────────────────────────────────────────────────────────
export const getRefills         = (day)   => api.get('/refills', { params: { day } });
export const createRefill       = (d)     => api.post('/refills', d);
export const updateRefillStatus = (id, s) => api.put(`/refills/${id}/status`, { status: s });
export const deleteRefill       = (id)    => api.delete(`/refills/${id}`);

export default api;